<script>
import { nextTick } from 'vue'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import apiSensors from '@/api/api-sensors.js'
import apiUsers from '@/api/api-users.js'

import Table from './common/SortedTable.vue'
import EditForm from './common/EditForm.vue'
import MessageBoxQuestion from './common/MessageBoxQuestion.vue'

export default {
    name: 'SensorsAdminComponent',
    
    components: {
        Table,
        EditForm,
        MessageBoxQuestion
    },

    props: {
        userLanguage: {
            type: String,
            default: 'cz'
        },
        languageStrings: {
            type: Object,
            default: () => {}
        },
        userId: {
            type: Number,
            defaults: -1
        }
    },

    emits: ['form-closed'],

    computed: {
        headers() {
            return [
                {
                    id: 'sn',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor013 != null ? this.languageStrings.ui.sensor013 : 'SN',
                    sort: true
                },
                {
                    id: 'name',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor025 != null ? this.languageStrings.ui.sensor025 : 'Název',
                    sort: true
                },
                {
                    id: 'customer',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor014 != null ? this.languageStrings.ui.sensor014 : 'Zákazník',
                    sort: true
                },
                {
                    id: 'parameters',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor015 != null ? this.languageStrings.ui.sensor015 : 'Parametry',
                    sort: true
                },
                {
                    id: 'valid-from',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor016 != null ? this.languageStrings.ui.sensor016 : 'Platnost od',
                    sort: true
                },
                {
                    id: 'valid-to',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor017 != null ? this.languageStrings.ui.sensor017 : 'Platnost do',
                    sort: true
                },
                {
                    id: 'customer-edit',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor018 != null ? this.languageStrings.ui.sensor018 : 'Editace zák.',
                    sort: true
                }
            ]
        },
        rows() {
            let r = []
            for (let i = 0; i < this.filteredSensors.length; i++) {
                r.push({
                    id: this.filteredSensors[i].id,
                    selected: this.selectedSensors.length > 0 && this.filteredSensors[i].id === this.selectedSensors[0] ? true : false,
                    cells: [
                        {
                            id: 'sn',
                            contenttype: 'text',
                            content: this.filteredSensors[i].serialNumber,
                        },
                        {
                            id: 'name',
                            contenttype: 'text',
                            content: this.filteredSensors[i].name,
                        },
                        {
                            id: 'customer',
                            contenttype: 'text',
                            content: this.filteredSensors[i].customer.name,
                        },
                        {
                            id: 'parameters',
                            contenttype: 'text',
                            content: this.filteredSensors[i].parameterSet,
                        },
                        {
                            id: 'valid-from',
                            contenttype: 'text',
                            content: this.formatDateTime(this.filteredSensors[i].validFrom),
                        },
                        {
                            id: 'valid-to',
                            contenttype: 'text',
                            content: this.filteredSensors[i].validTo != null ? this.formatDateTime(this.filteredSensors[i].validTo) : '',
                        },
                        {
                            id: 'customer-edit',
                            contenttype: 'text',
                            content: this.filteredSensors[i].customerEdit != null ? this.filteredSensors[i].customerEdit ? (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.form003 != null ? this.languageStrings.ui.form003 : 'Ano') : (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.form004 != null ? this.languageStrings.ui.form004 : 'Ne') : (this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.form004 != null ? this.languageStrings.ui.form004 : 'Ne')
                        }
                    ]
                })
            }
            return r
        },
        filteredSensors() {
            let sens = this.sensors
            if (this.filterValues.serialNumber.length > 0) {
                sens = sens.filter(s => s.serialNumber != null && s.serialNumber === this.filterValues.serialNumber)                   
            }
            if (this.filterValues.name.length > 0) {
                sens = sens.filter(s => s.name != null && s.name === this.filterValues.name)                   
            }
            if (this.filterValues.customer > 0) {
                sens = sens.filter(s => s.customer != null && s.customer.id === this.filterValues.customer)
            }
            return sens
        }
    },

    data() {
        return {
            saveButtonText: '',
            showEditForm: false,
            editFormTitle: '',
            editFormData: null,
            showSaveQuestion: false,
            selectedSensors: [],
            sensors: [],
            showMessageBoxQuestion: false,
            messageBoxQuestionText: '',
            messageBoxType: -1,
            messageBoxData: null,
            filterValues: { serialNumber: '', name: '', customer: 0 }
        }
    },    

    async mounted() {
        await this.loadSensors()

        await nextTick()
        document.getElementById('form').focus()
    },

    methods: {
        formatDateTime(date) {
            return date !== null ? dayjs(date + 'Z').utcOffset(0).format('D.M.YYYY H:mm') : ''
        },
        handleFormKeyDown(event) {
            if (event.key === 'Escape') {
                this.closeForm()
            }
        },
        closeForm() {
            this.$emit('form-closed')
        },

        async loadSensors() {
            this.sensors = []
            let response = await apiSensors.getSensors()
            if (response.code === 0) {
                this.sensors = response.data
            }
        },

        async newSensor() {
            this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor010 != null ? this.languageStrings.ui.sensor010 : 'Nový senzor'
            this.saveButtonText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor009 != null ? this.languageStrings.ui.sensor009 : 'Uložit'
            this.showSaveQuestion = true            
            await this.getEditFormData(-1)
            this.showEditForm = true
        },

        async editSensor() {
            if (this.selectedSensors.length > 0) {
                this.editFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor011 != null ? this.languageStrings.ui.sensor011 : 'Editace senzoru'
                this.saveButtonText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor009 != null ? this.languageStrings.ui.sensor009 : 'Uložit'
                this.showSaveQuestion = true            
                await this.getEditFormData(this.selectedSensors[0])
                this.showEditForm = true
            }
        },

        async deleteSensor() {
            if (this.selectedSensors.length > 0) {
                this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor012 != null ? this.languageStrings.ui.sensor012 : 'Smazat senzor?'
                this.messageBoxType = 1
                this.showMessageBoxQuestion = true
            }
        },

        async getEditFormData(sId) {
            let sensor = this.sensors.find(s => s.id === sId)
            this.editFormData = {
                id: sId,
                type: 2,
                rows: [
                    {
                        name: 'sn',
                        type: 'text',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor013 != null ? this.languageStrings.ui.sensor013 : 'SN',
                        value: sensor != null ? sensor.serialNumber : '',
                        required: true
                    },
                    {
                        name: 'name',
                        type: 'text',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor025 != null ? this.languageStrings.ui.sensor025 : 'Název',
                        value: sensor != null && sensor.name != null ? sensor.name : '',
                        required: true
                    },
                    {
                        name: 'customer',
                        type: 'options',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor014 != null ? this.languageStrings.ui.sensor014 : 'Zákazník',
                        options: [{ key: 0, value: '' }],
                        value: sensor != null ? sensor.customer.id : 0,
                        required: true
                    },
                    {
                        name: 'parameters',
                        type: 'options',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor015 != null ? this.languageStrings.ui.sensor015 : 'Parametry',
                        options: [{ key: '0', value: '' }],
                        value: sensor != null ? sensor.parameterSet : '0',
                        required: true
                    },
                    {
                        name: 'valid-from',
                        type: 'datetime',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor016 != null ? this.languageStrings.ui.sensor016 : 'Platnost od',
                        value: sensor != null ? sensor.validFrom : null,
                        required: true
                    },
                    {
                        name: 'valid-to',
                        type: 'datetime',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor017 != null ? this.languageStrings.ui.sensor017 : 'Platnost do',
                        value: sensor != null && sensor.validTo != null ? sensor.validTo : null
                    },
                    {
                        name: 'customer-edit',
                        type: 'checkbox',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor018 != null ? this.languageStrings.ui.sensor018 : 'Editace zák.',
                        value: sensor != null && sensor.customer != null ? sensor.customerEdit : false
                    }
                ]
            }

            let response = await apiUsers.getFlatFolders(this.userId)
            if (response.code === 0) {
                let customers = response.data.slice().sort((a, b) => a.name.localeCompare(b.name))
                this.editFormData.rows[2].options.push(...customers.map(c => ( { key: c.id, value: c.name } )))
            }

            response = await apiSensors.getParameterSets()
            if (response.code === 0) {
                this.editFormData.rows[3].options.push(...response.data.map(s => ( { key: s.id, value: s.id } )))
            }
        },

        filter() {
            this.editFormTitle = 'Filtr'
            this.saveButtonText = 'Filtrovat'
            this.showSaveQuestion = false

            this.editFormData = {
                id: -1,
                type: 1,
                rows: [
                    {
                        name: 'serial-number',
                        type: 'options',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor013 != null ? this.languageStrings.ui.sensor013 : 'SN',
                        options: [{ key: '', value: '' }],
                        value: this.filterValues.serialNumber
                    },
                    {
                        name: 'name',
                        type: 'options',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor025 != null ? this.languageStrings.ui.sensor025 : 'Název',
                        options: [{ key: '', value: '' }],
                        value: this.filterValues.name
                    },
                    {
                        name: 'customer',
                        type: 'options',
                        text: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.sensor014 != null ? this.languageStrings.ui.sensor014 : 'Zákazník',
                        options: [{ key: 0, value: '' }],
                        value: this.filterValues.customer
                    }
                ]
            }

            this.editFormData.rows[0].options.push(...[...new Map(this.sensors.map(item => [item.serialNumber, item])).values()].sort((a, b) => a.serialNumber.localeCompare(b.serialNumber)).map(s => ( { key: s.serialNumber, value: s.serialNumber } )))
            this.editFormData.rows[1].options.push(...[...new Map(this.sensors.filter(s => s.name != null && s.name.length > 0).map(item => [item.name, item])).values()].sort((a, b) => a.name.localeCompare(b.name)).map(s => ( { key: s.name, value: s.name } )))
            this.editFormData.rows[2].options.push(...[...new Map(this.sensors.map(item => [item.customer.name, item])).values()].sort((a, b) => a.customer.name.localeCompare(b.customer.name)).map(s => ( { key: s.customer.id, value: s.customer.name } )))

            this.showEditForm = true
        },

        async editFormClosed(result, data) {
            this.showEditForm = false

            if (result === 0) {
                switch (data.type) {
                    case 1: // Filter
                        this.selectedSensors = []
                        this.filterValues = { serialNumber: data.rows[0].value, name: data.rows[1].value, customer: data.rows[2].value }
                        break

                    case 2: // User
                    {
                        // End older sensors validity
                        let old = this.sensors.slice().filter(s => s.serialNumber === data.rows[0].value && s.validFrom < data.rows[3].value && s.validTo == null)
                        for (let i = 0; i < old.length; i++) {
                            old[i].validTo = dayjs(data.rows[3].value + 'Z').utcOffset(0).add(-1, 'minute')
                            await apiSensors.putSensor(old[i])
                        }
                        
                        if (data.id === -1) { // new
                            let result = await apiSensors.postSensor({
                                serialNumber: data.rows[0].value,
                                name: data.rows[1].value,
                                customer: { id: data.rows[2].value, name: '', children: [] },
                                parameterSet: data.rows[3].value,
                                validFrom: data.rows[4].value,
                                validTo: data.rows[5].value != null && data.rows[5].value.length > 0 ? data.rows[5].value : null,
                                customerEdit: data.rows[6].value
                            })
                            if (result.code === 0) {
                                await this.loadSensors()
                                this.selectedSensors = [ result.data.id ]
                            }
                        }
                        else { // edit
                            let result = await apiSensors.putSensor({
                                id: data.id,
                                serialNumber: data.rows[0].value,
                                name: data.rows[1].value,
                                customer: { id: data.rows[2].value, name: '', children: [] },
                                parameterSet: data.rows[3].value,
                                validFrom: data.rows[4].value,
                                validTo: data.rows[5].value != null && data.rows[5].value.length > 0 ? data.rows[5].value : null,
                                customerEdit: data.rows[6].value
                            })
                            if (result.code === 0) {
                                await this.loadSensors()
                                this.selectedSensors = [ result.data.id ]
                            }
                        }
                        break
                    }
                }
            }
        },

        setSelectedSensors(value) {
            this.selectedSensors = value
        },

        async messageBoxQuestionClosed(result) {
            this.showMessageBoxQuestion = false    

            if (result !== 0) {
                return
            }

            await apiSensors.deleteSensor(this.selectedSensors[0])
            await this.loadSensors()
        }
    }
}
</script>

<template>
    <div class="form table-form users-form">
        <div id="form" @keydown="handleFormKeyDown" tabindex="-1">
            <span class="title">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor005 != null ? languageStrings.ui.sensor005 : 'Senzory' }}</span>
            <img src="@/assets/images/x.svg" @click="closeForm()" />

            <div class="form-wrapper">
                <div class="form-content rows gap-10">
                    <div class="cols justify-space-between">
                        <span class="cols gap-5">
                            <button class="button-small" @click="newSensor()">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor006 != null ? languageStrings.ui.sensor006 : 'Nový' }}</button>
                            <button class="button-small" @click="editSensor()">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor007 != null ? languageStrings.ui.sensor007 : 'Editovat' }}</button>
                            <button class="button-small" @click="deleteSensor()">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.sensor008 != null ? languageStrings.ui.sensor008 : 'Smazat' }}</button>
                        </span>
                        <button class="button-small" @click="filter()">...</button>
                    </div>
                    <div class="scrollable">
                        <Table :headers="headers" :rows="rows" @selected-items="setSelectedSensors" />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Edit form - user, filter -->
    <EditForm v-if="showEditForm" :title="editFormTitle" @form-closed="editFormClosed" :edit-data="editFormData" :user-language="userLanguage"
        :saveButtonText="saveButtonText"
        :cancelButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form002 != null ? languageStrings.ui.form002 : 'Zrušit'"
        :yesButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form003 != null ? languageStrings.ui.form003 : 'Ano'"
        :noButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form004 != null ? languageStrings.ui.form004 : 'Ne'"
        :saveText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form005 != null ? languageStrings.ui.form005 : 'Uložit změny?'"
        :showQuestion="showSaveQuestion"
    />
    <!-- Edit form - user, filter -->


    <!-- Message box - Question -->
    <MessageBoxQuestion v-if="showMessageBoxQuestion" :text="messageBoxQuestionText" @form-closed="messageBoxQuestionClosed" :type="messageBoxType" :data="messageBoxData"
        :yesButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form003 != null ? languageStrings.ui.form003 : 'Ano'"
        :noButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form004 != null ? languageStrings.ui.form004 : 'Ne'"
    />
    <!-- Message box - Question -->
</template>